/**
 * Rigid variation-specific image lock for CheepSweepz catalog + detail views.
 */

export function normalizeVariationMatrix(raw) {
  if (!raw || typeof raw !== "object") {
    return { options: [], variants: [], asset_map: {}, default_key: null, locked: false };
  }
  return {
    options: Array.isArray(raw.options) ? raw.options : [],
    variants: Array.isArray(raw.variants) ? raw.variants : [],
    asset_map: raw.asset_map && typeof raw.asset_map === "object" ? raw.asset_map : {},
    default_key: typeof raw.default_key === "string" ? raw.default_key : null,
    locked: Boolean(raw.locked),
  };
}

export function buildVariationKey(optionValues = {}) {
  return Object.entries(optionValues)
    .filter(([, value]) => Boolean(String(value || "").trim()))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => `${name}:${String(value).trim()}`)
    .join("|");
}

export function getDefaultVariationSelection(matrix) {
  const normalized = normalizeVariationMatrix(matrix);
  if (!normalized.default_key) {
    return normalized.variants[0]?.option_values || {};
  }
  const out = {};
  for (const part of normalized.default_key.split("|")) {
    const idx = part.indexOf(":");
    if (idx <= 0) continue;
    out[part.slice(0, idx)] = part.slice(idx + 1);
  }
  return out;
}

export function resolveVariationImage(matrix, selected = {}) {
  const normalized = normalizeVariationMatrix(matrix);
  if (!normalized.locked) return null;

  const exactKey = buildVariationKey(selected);
  if (exactKey && normalized.asset_map[exactKey]) return normalized.asset_map[exactKey];

  const partialKeys = Object.entries(selected)
    .filter(([, value]) => Boolean(String(value || "").trim()))
    .map(([name, value]) => buildVariationKey({ [name]: value }))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const key of partialKeys) {
    if (normalized.asset_map[key]) return normalized.asset_map[key];
  }

  if (normalized.default_key && normalized.asset_map[normalized.default_key]) {
    return normalized.asset_map[normalized.default_key];
  }

  return normalized.variants[0]?.image_url || null;
}

export function isVariationLocked(product) {
  return Boolean(normalizeVariationMatrix(product?.variation_matrix).locked);
}

export function findSelectionForImage(matrix, url) {
  const normalized = normalizeVariationMatrix(matrix);
  if (!normalized.locked || !url) return null;

  for (const variant of normalized.variants) {
    if (variant.image_url === url) return { ...variant.option_values };
  }

  for (const [key, imageUrl] of Object.entries(normalized.asset_map)) {
    if (imageUrl !== url) continue;
    const out = {};
    for (const part of key.split("|")) {
      const idx = part.indexOf(":");
      if (idx <= 0) continue;
      out[part.slice(0, idx)] = part.slice(idx + 1);
    }
    if (Object.keys(out).length > 0) return out;
  }

  return null;
}

export function galleryFromVariationMatrix(matrix) {
  const normalized = normalizeVariationMatrix(matrix);
  if (!normalized.locked) return [];
  const seen = new Set();
  const urls = [];
  for (const url of Object.values(normalized.asset_map)) {
    if (!url || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  for (const variant of normalized.variants) {
    if (variant.image_url && !seen.has(variant.image_url)) {
      seen.add(variant.image_url);
      urls.push(variant.image_url);
    }
  }
  return urls;
}

export function getLockedProductImage(product, selectedOptions = null) {
  const matrix = normalizeVariationMatrix(product?.variation_matrix);
  if (matrix.locked) {
    const selection = selectedOptions || getDefaultVariationSelection(matrix);
    const locked = resolveVariationImage(matrix, selection);
    if (locked) return locked;
  }
  return null;
}

export function buildVariationGallery(product, selectedOptions = null) {
  const matrix = normalizeVariationMatrix(product?.variation_matrix);
  const locked = getLockedProductImage(product, selectedOptions);
  const matrixGallery = galleryFromVariationMatrix(matrix);
  if (locked) {
    return [locked, ...matrixGallery.filter((url) => url !== locked)];
  }
  return matrixGallery;
}
